/**
 * Модель доступа стенда приходит с бэкенда (GET /api/platform): один образ
 * фронта подходит любому стенду и не может разойтись с API по режиму.
 *
 * Это единственное место, которое знает о модулях моделей. Остальной код
 * берёт модуль через useAccess() и рисует его слоты (см. AccessModule.ts).
 */

import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { z } from 'zod';
import { AuthLoading } from '../AuthContext';
import { APIRequestGet } from '../utils/APIRequests';
import { AccessModel, AccessModule } from './AccessModule';
import deviceAccess from './device';
import subscriptionAccess from './subscription';

const MODULES: Record<AccessModel, AccessModule> = {
    Device: deviceAccess,
    Subscription: subscriptionAccess
};

const PlatformSchema = z.object({
    accessModel: z.enum(['Device', 'Subscription'])
});

const AccessContext = createContext<AccessModule | null>(null);

export function PlatformAccessProvider({ children }: { children: ReactNode }): ReactNode {
    const [access, setAccess] = useState<AccessModule | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        APIRequestGet('/platform', {
            schema: PlatformSchema
        }).then((result) => {
            setAccess(MODULES[result.data.accessModel]);
        }).catch(() => {
            // Угадывать модель нельзя: на стенде подписок модуль устройств
            // показал бы привязку кнопкой, которой там нет.
            setFailed(true);
        });
    }, []);

    if (failed) {
        return (
            <div className="form-page">
                <div className="form-page-container">
                    <div className="console-logo">Сервер недоступен, обновите страницу позже</div>
                </div>
            </div>
        );
    }

    if (!access) {
        return <AuthLoading />;
    }

    return (
        <AccessContext.Provider value={access}>
            {children}
        </AccessContext.Provider>
    );
}

export function useAccess(): AccessModule {
    const access = useContext(AccessContext);

    if (!access) {
        throw new Error("useAccess must be used inside PlatformAccessProvider");
    }

    return access;
}
